import { useEffect, useMemo, useState } from 'react';

import { History as HistoryIcon } from '@mui/icons-material';
import {
  Alert,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  IconButton,
  MenuItem,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';

import { FEATURES } from '@/config/features';
import { useHasRole } from '@/lib/hooks/useHasRole';
import {
  SETTING_LABELS,
  type SystemSetting,
  listSettings,
  updateSetting,
} from '@/lib/api/settings';

import { SettingHistoryDialog } from '@/components/settings/SettingHistoryDialog';

function extractError(e: unknown): string {
  if (e instanceof AxiosError) {
    const msg = (e.response?.data as { message?: string | string[] } | undefined)?.message;
    return Array.isArray(msg) ? msg.join(', ') : (msg ?? '저장 중 오류가 발생했습니다');
  }
  return '알 수 없는 오류가 발생했습니다';
}

interface RowProps {
  setting: SystemSetting;
  canEdit: boolean;
  onOpenHistory: () => void;
}

function SettingRow({ setting, canEdit, onOpenHistory }: RowProps) {
  const queryClient = useQueryClient();
  const meta = SETTING_LABELS[setting.key];
  const [draft, setDraft] = useState(setting.value);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  // 서버 갱신 시 입력 초기값 동기화
  useEffect(() => {
    setDraft(setting.value);
  }, [setting.value]);

  const mutation = useMutation({
    mutationFn: () => updateSetting(setting.key, draft.trim()),
    onSuccess: () => {
      setError(null);
      setSuccess(true);
      void queryClient.invalidateQueries({ queryKey: ['settings'] });
      void queryClient.invalidateQueries({ queryKey: ['settings', 'history', setting.key] });
      setTimeout(() => setSuccess(false), 2000);
    },
    onError: (e) => setError(extractError(e)),
  });

  const dirty = draft.trim() !== setting.value;

  return (
    <Card variant="outlined">
      <CardContent>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={2}
          alignItems={{ md: 'center' }}
          justifyContent="space-between"
        >
          <Stack spacing={0.25} sx={{ flex: 1, minWidth: 0 }}>
            <Stack direction="row" spacing={1} alignItems="center">
              <Typography variant="subtitle2" fontWeight={700}>
                {meta?.label ?? setting.key}
              </Typography>
              <Chip
                size="small"
                label={setting.valueType}
                variant="outlined"
                sx={{ height: 18, fontSize: '0.7rem' }}
              />
            </Stack>
            <Typography variant="caption" color="text.secondary">
              {meta?.description ?? setting.key}
            </Typography>
          </Stack>
          <Stack direction="row" spacing={1} alignItems="center">
            {setting.valueType === 'BOOLEAN' ? (
              <TextField
                size="small"
                select
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                disabled={!canEdit || mutation.isPending}
                sx={{ minWidth: 110 }}
              >
                <MenuItem value="true">true</MenuItem>
                <MenuItem value="false">false</MenuItem>
              </TextField>
            ) : (
              <TextField
                size="small"
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                disabled={!canEdit || mutation.isPending}
                error={!!error}
                sx={{ minWidth: 110 }}
                slotProps={{
                  htmlInput: {
                    inputMode:
                      setting.valueType === 'INTEGER' || setting.valueType === 'FLOAT'
                        ? 'numeric'
                        : 'text',
                  },
                }}
              />
            )}
            <Button
              variant="contained"
              size="small"
              disabled={!canEdit || !dirty || mutation.isPending}
              onClick={() => mutation.mutate()}
            >
              {mutation.isPending ? '저장 중…' : '저장'}
            </Button>
            <Tooltip title="변경 이력">
              <IconButton size="small" onClick={onOpenHistory}>
                <HistoryIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Stack>
        </Stack>
        {error && (
          <Alert severity="error" sx={{ mt: 1.5 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}
        {success && (
          <Alert severity="success" sx={{ mt: 1.5 }}>
            저장됨
          </Alert>
        )}
      </CardContent>
    </Card>
  );
}

/**
 * 의뢰서 7장 시스템 설정 화면 (P7-3).
 *
 * - 항목별 인라인 편집 (TextField/Select + 저장 버튼)
 * - 변경 시 dirty=true 일 때만 저장 활성
 * - 클라이언트는 형식 강제하지 않음 — 서버가 valueType에 따라 400 반환
 * - 각 항목 우측 History 아이콘 → 변경 이력 모달
 * - 편집 권한은 ADMIN만 (사이드바도 ADMIN에만 노출)
 */
export function SettingsPage() {
  const canEdit = useHasRole('ADMIN');

  const [historyKey, setHistoryKey] = useState<string | null>(null);

  const { data, isLoading, isError } = useQuery({
    queryKey: ['settings'],
    queryFn: listSettings,
    staleTime: 30 * 1000,
  });

  // 외래 유효 기간은 외래 기능이 꺼져 있는 동안 감춘다 — FEATURES.outpatient 참고.
  // 쓰지 못하는 기능의 정책만 남겨두면 무엇에 영향을 주는 값인지 알 수 없다.
  const visibleSettings = (data ?? []).filter(
    (s) => FEATURES.outpatient || !s.key.startsWith('outpatient.'),
  );

  const historyLabel = useMemo(() => {
    if (!historyKey) return '';
    return SETTING_LABELS[historyKey]?.label ?? historyKey;
  }, [historyKey]);

  return (
    <Stack spacing={3}>
      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <Typography variant="h4">시스템 설정</Typography>
        {!canEdit && <Chip size="small" label="조회 전용" variant="outlined" />}
      </Stack>

      {isError && <Alert severity="error">설정을 불러오지 못했습니다</Alert>}

      {isLoading ? (
        <Stack alignItems="center" sx={{ py: 4 }}>
          <CircularProgress size={24} />
        </Stack>
      ) : (
        <Stack spacing={2}>
          {visibleSettings.map((s) => (
            <SettingRow
              key={s.key}
              setting={s}
              canEdit={canEdit}
              onOpenHistory={() => setHistoryKey(s.key)}
            />
          ))}
        </Stack>
      )}

      <SettingHistoryDialog
        settingKey={historyKey}
        label={historyLabel}
        onClose={() => setHistoryKey(null)}
      />
    </Stack>
  );
}
